import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, match } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormikValues } from 'formik';
import { UserCodeError, UserParams } from 'types';

import ModifyUserForm from 'components/templates/form-modify-user';
import CreateUserForm from 'components/templates/form-create-user';
import Layout from 'components/templates/layout';
import { useIntl } from 'common/config/i18n';
import {
  UserActions,
  UserSelectors,
  UserInfoToModifySelectors,
  UserInfoToModifyActions,
  UserInfoSelectors,
  ClearStateSliceActions,
  ServiceTypesSelectors,
  ProfilesSelectors,
  ProfilesActions,
} from 'common/config/store';
import history from 'app-router/history';
import Card from 'components/molecules/card';
import Notification from 'components/organisms/notification';

interface Props {
  match: match<{
    userName: string;
  }>;
}

const Wrapper = styled.div`
  margin: 5px;
`;

const StyledBreadcrumbWrapper = styled.div`
  margin: 10px 0 20px 20px;
`;

const User: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { t } = useIntl();
  const { userName } = props.match.params;

  const [userCodeError, setUserCodeError] = useState<UserCodeError | undefined>(undefined);

  const isCurrentUser = useSelector(UserSelectors.isCurrentUserSelector);
  const userStatus = useSelector(UserSelectors.statusSelector);
  const userError = useSelector(UserSelectors.errorSelector);
  const userInfoToModify = useSelector(UserInfoToModifySelectors.dataSelector);
  const userInfoToModifyStatus = useSelector(UserInfoToModifySelectors.statusSelector);
  const userInfos = useSelector(UserInfoSelectors.dataSelector);
  const profils = useSelector(ProfilesSelectors.profilesSelector);
  const serviceTypes = useSelector(ServiceTypesSelectors.serviceTypesSelector);

  useEffect(() => {
    if (isCurrentUser) {
      return Notification('info', t('notif', 'NOTIF_DECONNEXION'), '');
    }
    if (userName) {
      dispatch(UserInfoToModifyActions.fetch(userName));
    }
  }, [dispatch, isCurrentUser, t, userName]);

  useEffect(() => {
    dispatch(ProfilesActions.fetchPofiles());

    return () => {
      dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User', sliceKey: 'error' }));
      dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User', sliceKey: 'status' }));
    };
  }, [dispatch]);

  const onCreateHandler = (values: UserParams) => {
    dispatch(
      UserActions.createUser(
        {
          ...values,
        },
        (result) => {
          if (!result.cancelled && !result.error) {
            history.push('/users');
          } else {
            setUserCodeError(result?.error?.content?.message as UserCodeError);
          }
        }
      )
    );
  };

  const onUpdateHandler = (values: FormikValues) => {
    return dispatch(
      UserActions.updateUser(
        {
          ...values,
        },
        (result) => {
          if (!result.cancelled && !result.error) {
            history.push('/users');
          } else {
            setUserCodeError(result?.error?.content?.message as UserCodeError);
          }
        }
      )
    );
  };

  const title = userName ? t('UserPage', 'USER_UPDATE') : t('UserPage', 'USER_CREATE');

  return (
    <div>
      <Layout />
      <Wrapper>
        <StyledBreadcrumbWrapper>
          <Link to="/users">{t('UserPage', 'USER_BACK')}</Link>
        </StyledBreadcrumbWrapper>
        <Card title={title}>
          {userName ? (
            <>
              {userInfoToModify && userInfoToModify?.id && userInfos?.id && (
                <ModifyUserForm
                  onUpdate={onUpdateHandler}
                  profils={profils}
                  serviceTypes={serviceTypes}
                  firstName={userInfoToModify?.firstName}
                  lastName={userInfoToModify?.lastName}
                  userName={userInfoToModify?.userName}
                  email={userInfoToModify?.email}
                  trainTypeIDs={userInfoToModify?.trainTypeIDs}
                  groupName={userInfoToModify?.groupName}
                  profileID={userInfoToModify?.profileID ? Number(userInfoToModify?.profileID) : undefined}
                  id={userInfoToModify.id.toString()}
                  isUpdatingCurrentUser={userInfos.id === userInfoToModify?.id}
                  userError={userError}
                  userStatus={userStatus}
                  userInfoToModifyStatus={userInfoToModifyStatus}
                  userCodeError={userCodeError}
                />
              )}
            </>
          ) : (
            <CreateUserForm
              onCreate={onCreateHandler}
              profils={profils}
              serviceTypes={serviceTypes}
              error={userError}
              status={userStatus}
              userCodeError={userCodeError}
            />
          )}
        </Card>
      </Wrapper>
    </div>
  );
};

export default User;
